import { ReactElement, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { ReactComponent as AddIcon } from 'assets/icons/addIcon.svg';
import { AddAndExtendActions, Backdrop, GoBackButtonWithTitle, NewConfirmDialog } from 'components';
import { achievementsTabs } from 'features/career/constants';
import { CreateButton } from 'features/documentCategories/components/DocumentCategories/styled';
import { useModal, useWindowSize } from 'hooks';
import { theme } from 'styles';
import { AchievementsTabsValues } from 'types';

import { AchievementEmptyState, AchievementsList, TabPanel, Tabs } from '../../components';
import { useAchievementsPage, useAchievementsTabs } from './hooks';
import { EmptyStateW, PageW, ContentW, Header } from './styled';
import { accomplishedDialogSx } from './styles';

interface LocationI extends Omit<Location, 'state'> {
  state?: {
    accomplishedAchievement?: string;
  };
}

export function AchievementsPage(): ReactElement {
  const { t } = useTranslation();
  const { isMobile } = useWindowSize();
  const location = useLocation() as unknown as LocationI;
  const navigate = useNavigate();
  const { handleCloseModal: handleCloseAchievementModal } = useModal();
  const { activeTab, handleChange } = useAchievementsTabs();

  const achievementDialogName = location?.state?.accomplishedAchievement ?? '';

  const handleCloseConfirmModal = useCallback(() => {
    handleCloseAchievementModal();
    navigate(location, {
      replace: true,
    });
  }, [handleCloseAchievementModal, location, navigate]);

  const {
    isLoading,
    activeAchievements,
    accomplishedAchievements,
    isAchievementsList,
    isActiveAchievementsList,
    isAccomplishedAchievementsList,
    employeeId,
    addAchievementLink,
    usersList,
  } = useAchievementsPage();

  return (
    <>
      <PageW>
        <Header>
          {isMobile ? (
            <AddAndExtendActions addIconLink={addAchievementLink} />
          ) : (
            <>
              <GoBackButtonWithTitle title={t('career.achievements.title')} />
              <CreateButton
                startIcon={<AddIcon style={{ fill: theme.palette.common.white }} />}
                onClick={() => navigate(addAchievementLink, { state: { prev: location.pathname } })}
                style={{
                  height: '56px',
                  padding: '16px',
                  fontSize: '14px',
                  backgroundColor: theme.palette.info.main,
                  color: theme.palette.common.white,
                  borderRadius: '8px',
                  fontWeight: 400,
                  textTransform: 'none',
                  width: '200px',
                }}
              >
                {t('career.achievements.addNewGoal')}
              </CreateButton>
            </>
          )}
        </Header>

        <ContentW>
          {isLoading ? (
            <Backdrop />
          ) : isAchievementsList ? (
            <>
              <Tabs
                activeTab={activeTab}
                tabs={achievementsTabs}
                isAccomplishedTabDisabled={!isAccomplishedAchievementsList}
                handleChange={handleChange}
              />
              <TabPanel path={AchievementsTabsValues.ACTIVE} value={activeTab}>
                {isActiveAchievementsList ? (
                  <AchievementsList
                    data={activeAchievements}
                    isFullView
                    employeeId={employeeId}
                    usersList={usersList}
                  />
                ) : (
                  <EmptyStateW>
                    <AchievementEmptyState addAchievementLink={addAchievementLink} />
                  </EmptyStateW>
                )}
              </TabPanel>
              <TabPanel path={AchievementsTabsValues.ACCOMPLISHED} value={activeTab}>
                <AchievementsList
                  data={accomplishedAchievements}
                  isFullView
                  employeeId={employeeId}
                  usersList={usersList}
                />
              </TabPanel>
            </>
          ) : (
            <EmptyStateW>
              <AchievementEmptyState addAchievementLink={addAchievementLink} />
            </EmptyStateW>
          )}
        </ContentW>
      </PageW>

      {!!achievementDialogName && (
        <NewConfirmDialog
          title={t('career.achievements.accomplished.title', {
            achievementName: achievementDialogName,
          })}
          subTitle={t('career.achievements.accomplished.subtitle')}
          open={!!achievementDialogName}
          onConfirm={handleCloseConfirmModal}
          dialogSx={accomplishedDialogSx}
          submitLabel={t('career.achievements.accomplished.successButton')}
        />
      )}
    </>
  );
}
